import type { Role, SignatoryRole } from "../role/role.dto";
import type {
  AgencyValidationStep,
  ConventionDto,
  ConventionStatus,
} from "./convention.dto";

type StatusTransitionRefineParams = {
  convention: ConventionDto;
  agencyValidationSteps: AgencyValidationStep;
};

export type StatusTransitionConfig = {
  validInitialStatuses: ConventionStatus[];
  validRoles: Role[];
  refine?: (params: StatusTransitionRefineParams) => {
    isError: boolean;
    errorMessage: string;
  };
};

export const validSignatoryRoles: SignatoryRole[] = [
  "beneficiary",
  "beneficiary-representative",
  "beneficiary-current-employer",
  "establishment-representative",
];

export const statusTransitionConfigs: Record<
  ConventionStatus,
  StatusTransitionConfig
> = {
  READY_TO_SIGN: {
    validInitialStatuses: [
      "READY_TO_SIGN",
      "PARTIALLY_SIGNED",
      "IN_REVIEW",
      "ACCEPTED_BY_COUNSELLOR",
    ],
    validRoles: [
      "counsellor",
      "validator",
      "back-office",
      ...validSignatoryRoles,
    ],
    refine: ({ convention }) => {
      const renewedKey: keyof ConventionDto = "renewed";
      return {
        isError: renewedKey in convention,
        errorMessage: "Cannot edit a renewed convention",
      };
    },
  },

  PARTIALLY_SIGNED: {
    validInitialStatuses: ["READY_TO_SIGN", "PARTIALLY_SIGNED"],
    validRoles: validSignatoryRoles,
  },

  IN_REVIEW: {
    validInitialStatuses: ["PARTIALLY_SIGNED"],
    validRoles: validSignatoryRoles,
  },

  ACCEPTED_BY_COUNSELLOR: {
    validInitialStatuses: ["IN_REVIEW"],
    validRoles: ["counsellor"],
  },
  ACCEPTED_BY_VALIDATOR: {
    validInitialStatuses: ["IN_REVIEW", "ACCEPTED_BY_COUNSELLOR"],
    validRoles: ["validator"],
    refine: ({ convention, agencyValidationSteps }) => ({
      isError:
        convention.status === "IN_REVIEW" &&
        agencyValidationSteps === "counsellor-and-validator",
      errorMessage: `Vous ne pouvez pas valider la convention: '${convention.id}', elle doit d'abord être revue et marquée comme éligible par un conseiller.`,
    }),
  },

  // This config allows a counsellor to reject or cancel a Convention after it been
  // accepted by a validator. Should we be stricter? We assume that this is a
  // rare edge case that can be addressed at a later stage.
  REJECTED: {
    validInitialStatuses: [
      "READY_TO_SIGN",
      "PARTIALLY_SIGNED",
      "IN_REVIEW",
      "ACCEPTED_BY_COUNSELLOR",
    ],
    validRoles: ["counsellor", "validator", "back-office"],
  },
  CANCELLED: {
    validInitialStatuses: ["ACCEPTED_BY_VALIDATOR"],
    validRoles: ["validator", "back-office", "counsellor"],
  },

  DEPRECATED: {
    validInitialStatuses: [
      "ACCEPTED_BY_COUNSELLOR",
      "IN_REVIEW",
      "PARTIALLY_SIGNED",
      "READY_TO_SIGN",
    ],
    validRoles: ["counsellor", "validator", "back-office"],
  },
};
